import React, { useEffect, useMemo, useState } from "react";
import { api, resolvePhotoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Pencil, Camera, Upload } from "lucide-react";
import { toast } from "sonner";

const LAST_KEY = "admin_last_product_selection";
const emptyProduct = {
    name: "",
    description: "",
    price: "",
    discount_percent: 0,
    department: "",
    category: "",
    photo_url: "",
    is_available: true,
};

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyProduct);
    const [uploading, setUploading] = useState(false);
    const [filterDept, setFilterDept] = useState("all");
    const [filterCat, setFilterCat] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const [p, c, d] = await Promise.all([
                api.get("/products"),
                api.get("/categories"),
                api.get("/departments"),
            ]);
            setProducts(p.data);
            setCategories(c.data);
            setDepartments(d.data);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    // If selected filter-department changes, reset category filter when it no longer belongs.
    useEffect(() => {
        if (filterDept === "all") return;
        if (filterCat === "all") return;
        const cat = categories.find((c) => c.name === filterCat);
        if (!cat || cat.department !== filterDept) {
            setFilterCat("all");
        }
    }, [filterDept, filterCat, categories]);

    const filteredCats = useMemo(
        () => (filterDept === "all" ? categories : categories.filter((c) => c.department === filterDept)),
        [categories, filterDept]
    );

    const filteredProducts = useMemo(() => {
        return products.filter((p) => {
            if (filterDept !== "all" && p.department !== filterDept) return false;
            if (filterCat !== "all" && p.category !== filterCat) return false;
            return true;
        });
    }, [products, filterDept, filterCat]);

    const openNew = () => {
        setEditing(null);
        // Pre-fill last used department + category if available
        let last = {};
        try { last = JSON.parse(localStorage.getItem(LAST_KEY) || "{}"); } catch { /* noop */ }
        const dep = last.department && departments.some((d) => d.name === last.department)
            ? last.department
            : (departments[0]?.name || "");
        const catsForDep = categories.filter((c) => c.department === dep);
        const cat = last.category && catsForDep.some((c) => c.name === last.category)
            ? last.category
            : (catsForDep[0]?.name || "");
        setForm({ ...emptyProduct, department: dep, category: cat });
        setDialogOpen(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({
            name: p.name,
            description: p.description || "",
            price: String(p.price),
            discount_percent: p.discount_percent || 0,
            department: p.department || "",
            category: p.category,
            photo_url: p.photo_url || "",
            is_available: p.is_available,
        });
        setDialogOpen(true);
    };

    const uploadFile = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, photo_url: data.url }));
            toast.success("Photo uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const save = async () => {
        if (!form.name.trim() || !form.department || !form.category || !form.price) {
            toast.error("Name, price, department and category are required");
            return;
        }
        const payload = {
            name: form.name,
            description: form.description,
            price: parseFloat(form.price),
            discount_percent: parseFloat(form.discount_percent) || 0,
            department: form.department,
            category: form.category,
            photo_url: form.photo_url,
            is_available: form.is_available,
        };
        try {
            if (editing) {
                await api.put(`/products/${editing.id}`, payload);
                toast.success("Product updated");
            } else {
                await api.post("/products", payload);
                toast.success("Product created");
            }
            localStorage.setItem(LAST_KEY, JSON.stringify({
                department: form.department, category: form.category,
            }));
            setDialogOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.name}"?`)) return;
        try {
            await api.delete(`/products/${p.id}`);
            toast.success("Deleted");
            load();
        } catch {
            toast.error("Could not delete");
        }
    };

    return (
        <section data-testid="admin-products-page">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
                <h2 className="font-serif text-3xl">Products</h2>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="w-40">
                        <Select value={filterDept} onValueChange={setFilterDept}>
                            <SelectTrigger data-testid="products-filter-dept" className="rounded-full h-9">
                                <SelectValue placeholder="Department" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All departments</SelectItem>
                                {departments.map((d) => (
                                    <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-40">
                        <Select value={filterCat} onValueChange={setFilterCat}>
                            <SelectTrigger data-testid="products-filter-cat" className="rounded-full h-9">
                                <SelectValue placeholder="Category" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All categories</SelectItem>
                                {filteredCats.map((c) => (
                                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {(filterDept !== "all" || filterCat !== "all") && (
                        <button
                            type="button"
                            data-testid="products-filter-clear"
                            onClick={() => { setFilterDept("all"); setFilterCat("all"); }}
                            className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground px-2 py-1"
                        >
                            Clear
                        </button>
                    )}
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button
                            data-testid="admin-new-product-btn"
                            onClick={openNew}
                            className="rounded-full bg-primary hover:bg-primary-hover"
                        >
                            <Plus className="h-4 w-4 mr-1" />
                            New product
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg flex flex-col max-h-[90vh]">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-2xl">
                                {editing ? "Edit product" : "New product"}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 overflow-y-auto flex-1 pr-1">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em]">Photo</Label>
                                <div className="mt-1 flex items-center gap-4">
                                    <div className="h-24 w-24 rounded-2xl bg-surface/70 border border-border/60 overflow-hidden flex items-center justify-center">
                                        {form.photo_url ? (
                                            <img
                                                src={resolvePhotoUrl(form.photo_url)}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <Camera className="h-6 w-6 text-muted-foreground" />
                                        )}
                                    </div>
                                    <div className="flex-1 space-y-2">
                                        <label className="cursor-pointer inline-flex items-center gap-2 text-sm rounded-full border border-border/60 px-4 py-2 hover:border-foreground">
                                            <Upload className="h-4 w-4" />
                                            {uploading ? "Uploading…" : "Upload photo"}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                capture="environment"
                                                className="hidden"
                                                data-testid="product-photo-input"
                                                onChange={(e) => uploadFile(e.target.files?.[0])}
                                            />
                                        </label>
                                        <p className="text-xs text-muted-foreground">
                                            Or paste an image URL below.
                                        </p>
                                        <Input
                                            value={form.photo_url}
                                            onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                                            placeholder="https://…"
                                            data-testid="product-photo-url-input"
                                            className="h-9 rounded-xl text-xs"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em]">Name</Label>
                                <Input
                                    data-testid="product-name-input"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="mt-1 rounded-xl"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em]">Description</Label>
                                <Textarea
                                    data-testid="product-desc-input"
                                    rows={2}
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    className="mt-1 rounded-xl"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.2em]">Price (₹)</Label>
                                    <Input
                                        data-testid="product-price-input"
                                        type="number"
                                        step="0.01"
                                        value={form.price}
                                        onChange={(e) => setForm({ ...form, price: e.target.value })}
                                        className="mt-1 rounded-xl"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.2em]">Discount %</Label>
                                    <Input
                                        data-testid="product-discount-input"
                                        type="number"
                                        step="1"
                                        min="0"
                                        max="100"
                                        value={form.discount_percent || 0}
                                        onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                                        className="mt-1 rounded-xl"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.2em]">Available</Label>
                                    <div className="mt-2 flex items-center h-9">
                                        <Switch
                                            data-testid="product-available-switch"
                                            checked={form.is_available}
                                            onCheckedChange={(v) => setForm({ ...form, is_available: v })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.2em]">Department</Label>
                                    <Select
                                        value={form.department}
                                        onValueChange={(v) => {
                                            const cats = categories.filter((c) => c.department === v);
                                            setForm({ ...form, department: v, category: cats[0]?.name || "" });
                                        }}
                                    >
                                        <SelectTrigger
                                            data-testid="product-department-select"
                                            className="mt-1 rounded-xl"
                                        >
                                            <SelectValue placeholder="Pick department" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {departments.map((d) => (
                                                <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.2em]">Category</Label>
                                    <Select
                                        value={form.category}
                                        onValueChange={(v) => setForm({ ...form, category: v })}
                                    >
                                        <SelectTrigger
                                            data-testid="product-category-select"
                                            className="mt-1 rounded-xl"
                                        >
                                            <SelectValue placeholder="Pick category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {categories
                                                .filter((c) => !form.department || c.department === form.department)
                                                .map((c) => (
                                                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                                ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                data-testid="product-save-btn"
                                onClick={save}
                                className="rounded-full bg-primary hover:bg-primary-hover"
                            >
                                Save product
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            {loading ? (
                <p className="text-muted-foreground">Loading…</p>
            ) : departments.length === 0 ? (
                <div
                    data-testid="admin-products-need-department"
                    className="text-center py-16 text-muted-foreground border border-dashed border-border/60 rounded-2xl"
                >
                    Create at least one Department before adding products.
                </div>
            ) : categories.length === 0 ? (
                <div
                    data-testid="admin-products-need-category"
                    className="text-center py-16 text-muted-foreground border border-dashed border-border/60 rounded-2xl"
                >
                    Create at least one category before adding products.
                </div>
            ) : products.length === 0 ? (
                <div
                    data-testid="admin-products-empty"
                    className="text-center py-16 text-muted-foreground border border-dashed border-border/60 rounded-2xl"
                >
                    No products yet.
                </div>
            ) : filteredProducts.length === 0 ? (
                <div
                    data-testid="admin-products-no-match"
                    className="text-center py-16 text-muted-foreground border border-dashed border-border/60 rounded-2xl"
                >
                    No products match the current filters.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredProducts.map((p) => (
                        <article
                            key={p.id}
                            data-testid={`admin-product-${p.id}`}
                            className="bg-card border border-border/60 rounded-2xl overflow-hidden"
                        >
                            <div className="aspect-video bg-border/20 relative">
                                {p.photo_url ? (
                                    <img
                                        src={resolvePhotoUrl(p.photo_url)}
                                        alt={p.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : null}
                                {p.discount_percent > 0 && (
                                    <span
                                        data-testid={`admin-product-discount-${p.id}`}
                                        className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] font-semibold uppercase tracking-[0.15em] px-2 py-1 rounded-full shadow-sm"
                                    >
                                        -{p.discount_percent}%
                                    </span>
                                )}
                            </div>
                            <div className="p-4">
                                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                    {p.category}
                                </p>
                                <h3 className="font-serif text-lg mt-1">{p.name}</h3>
                                <div className="flex items-center justify-between mt-2">
                                    <span className="font-serif text-xl text-primary font-semibold">
                                        ₹{p.price.toFixed(2)}
                                    </span>
                                    <span
                                        className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full ${
                                            p.is_available
                                                ? "bg-moss/20 text-moss"
                                                : "bg-surface text-muted-foreground"
                                        }`}
                                    >
                                        {p.is_available ? "Live" : "Hidden"}
                                    </span>
                                </div>
                                <div className="flex gap-2 mt-4">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        data-testid={`admin-edit-${p.id}`}
                                        onClick={() => openEdit(p)}
                                        className="rounded-full"
                                    >
                                        <Pencil className="h-3 w-3 mr-1" />
                                        Edit
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        data-testid={`admin-delete-${p.id}`}
                                        onClick={() => remove(p)}
                                        className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                                    >
                                        <Trash2 className="h-3 w-3 mr-1" />
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </section>
    );
}
